import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { PiEyeFill, PiEyeSlashFill } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { registerUser } from "../../adapters";
import Loader from "../../components/Loader";
import {
  selectUserData,
  selectUserLoginState,
} from "../../config/redux/slices/userSlice/selectors";
import { fetchUser } from "../../config/redux/slices/userSlice/thunks";
import { storeAuthToken } from "../../utils/auth";
import { showErrors } from "../../utils/ui";
import { Container, StyledMainContainer } from "../Layout/styles";
import {
  StyledInputFields,
  StyledRegisterElements,
  StyledRegisterForm,
  StyledRegisterHeader,
  StyledRegisterPage,
} from "./styles";
import { setUserLoggedIn } from "../../config/redux/slices/userSlice";

const RegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const toggleShowPassword = () => setShowPassword(!showPassword);

  const userData = useSelector(selectUserData);
  const isUserLoggedIn = useSelector(selectUserLoginState);

  const { handleSubmit, handleBlur, handleChange, values, errors } = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Enter first name"),
      lastName: Yup.string().required("Enter last name"),
      email: Yup.string().email("Enter a valid email").required("Enter email"),
      password: Yup.string()
        .required("Enter password")
        .min(
          8,
          "Password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special"
        )
        .matches(/[0-9]/, "Password requires a number")
        .matches(/[a-z]/, "Password requires a lowercase letter")
        .matches(/[A-Z]/, "Password requires an uppercase letter")
        .matches(/[^\w]/, "Password requires a symbol"),
    }),
    onSubmit: async (values) => {
      setIsProcessing(true);
      const { email, password, firstName, lastName } = values;
      try {
        const response = await registerUser({
          email,
          password,
          firstName,
          lastName,
        });
        toast.success(response.message);
        storeAuthToken(response.data.authToken);
        dispatch(fetchUser());
      } catch (e) {
        showErrors(e);
      }
      setIsProcessing(false);
    },
  });

  useEffect(() => {
    switch (userData.state) {
      case "success":
        setIsProcessing(false);
        dispatch(setUserLoggedIn());
        break;
      case "failed":
        setIsProcessing(false);
        showErrors(userData.error);
        break;
      default:
        break;
    }
  }, [dispatch, userData]);

  useEffect(() => {
    if (isUserLoggedIn) {
      navigate("/app", { replace: true });
    }
  }, [isUserLoggedIn, navigate]);

  const navigateToLogin = () => navigate("/login", { replace: true });

  return isUserLoggedIn ? (
    <></>
  ) : (
    <Container>
      <StyledMainContainer>
        {isProcessing && <Loader showText="Registering" fullPage />}
        <StyledRegisterForm onSubmit={handleSubmit}>
          <StyledRegisterPage>
            <StyledRegisterHeader>
              <Typography variant="h4" color="teal" fontWeight="bold">
                Register to Psyming
              </Typography>
              <Typography variant="body1">Fill your details</Typography>
            </StyledRegisterHeader>
            <StyledInputFields>
              <TextField
                value={values.firstName}
                type="text"
                name="firstName"
                label="First Name"
                placeholder="Enter your firstName"
                error={!!errors.firstName}
                helperText={errors.firstName || " "}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <TextField
                value={values.lastName}
                type="text"
                name="lastName"
                label="Last Name"
                placeholder="Enter your lastName"
                error={!!errors.lastName}
                helperText={errors.lastName || " "}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <TextField
                value={values.email}
                type="email"
                name="email"
                label="Email"
                placeholder="Enter your email"
                error={!!errors.email}
                helperText={errors.email || " "}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <TextField
                value={values.password}
                type={showPassword ? "text" : "password"}
                name="password"
                label="Password"
                placeholder="Enter your password"
                error={!!errors.password}
                helperText={errors.password || " "}
                onChange={handleChange}
                onBlur={handleBlur}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={toggleShowPassword}
                        onMouseDown={toggleShowPassword}
                      >
                        {showPassword ? <PiEyeFill /> : <PiEyeSlashFill />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <StyledRegisterElements>
                <Button variant="contained" color="primary" type="submit">
                  Register to app
                </Button>
                <Button variant="text" onClick={navigateToLogin}>
                  Already registered? Login here
                </Button>
              </StyledRegisterElements>
            </StyledInputFields>
          </StyledRegisterPage>
        </StyledRegisterForm>
      </StyledMainContainer>
    </Container>
  );
};

export default RegisterPage;
